import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Label,
  Brush,
  Tooltip,
  Line,
  Bar,
  ComposedChart,
  Area,
  LabelList,
} from "recharts";
import {
  COLOR_CODES,
  isMobile,
  PrimaryColor,
  SecondaryColor,
} from "../../common";
import {
  BAR_RADIUS,
  BrushStart,
  ChartArguements,
  ChartType,
  LegendKey,
} from "../../models/chart_model";
import { COLORS } from "./ReCharts";
import { YAxisFormatter, renderQuarterTick } from "./components";
import { COST_UNIT } from "../../Units";
import { getTimeRange } from "./functions";

const getLegendColor = (
  legend: LegendKey,
  index: number,
  selectedLegends: LegendKey[],
  isLegend?: boolean
): string => {
  const isSelected =
    selectedLegends.some((item) => item.dataKey === legend.dataKey) ||
    selectedLegends.length === 0;
  return isSelected
    ? (isLegend ? legend.legendColor : legend.stroke) ??
        legend.stroke ??
        COLORS[index % COLORS.length]
    : "#E0E0E0";
};

export function lightenColor(color: string, percent: number): string {
  const num = parseInt(color.replace("#", ""), 16),
    amt = Math.round(2.55 * percent),
    R = (num >> 16) + amt,
    G = ((num >> 8) & 0x00ff) + amt,
    B = (num & 0x0000ff) + amt;

  return `#${(
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  )
    .toString(16)
    .slice(1)}`;
}

export const LegendComponent = (
  legend: LegendKey,
  index: number,
  selectedLegends: LegendKey[],
  handleLegendClick: (legend: LegendKey) => void,
  circleOnly?: boolean
) => (
  <div
    key={legend.dataKey}
    className={
      legend.type == ChartType.Bar || circleOnly ? "bar-legend" : "line-legend"
    }
    onClick={() => handleLegendClick(legend)}
  >
    <div
      style={{ color: getLegendColor(legend, index, selectedLegends, true) }}
    >
      <div
        className={
          legend.type === ChartType.Bar || circleOnly
            ? "dot"
            : legend.isDash
            ? "dash"
            : "line"
        }
        style={{
          marginRight: "6px",
          backgroundColor: getLegendColor(legend, index, selectedLegends, true),
          borderColor: getLegendColor(legend, index, selectedLegends, true),
        }}
      ></div>
      {legend.legendName ?? legend.name}
    </div>
  </div>
);

export function ReMixChart({
  data,
  legends,
  syncid,
  unit,
  xDataKey,
  onLegendChange,
  xTick,
  xLabel,
  yAxisLabel,
  secondYAxisLabel,
  yAxisWidth,
  legendBreakIndex,
  hidePrice,
  circleLegend = false,
  xAxisLabelPosition = "insideBottom",
  xAxisLabelOffset,
  onlyTitle = false,
  disableLegendClick = false,
  disableLegend = false,
  secondXDataKey,
  showBarLabel = false,
  showBrush = false,
  brushIndex,
  labelFontSize = 12,
  labelFormatter,
  barFontSize,
  nameFormatter,
  rendexSecondXTick,
  brushHeight = 30,
  brushStart = BrushStart.End,
  xaxisHeight = 30,
  secondYAxisWidth,
  perpendicularBarLab,
  secondXAxisHeight = 12,
  barCategoryGap = 2.5,
  xAxisTickMargin = 2,
  isTimeSlot,
  xAxisAngle,
  maxBarSize = 110,
  fontSize = 10,

  secondXAxisLabel,
}: ChartArguements) {
  const [selectedLegends, setSelectedLegends] = useState<LegendKey[]>([]);

  useEffect(() => {
    console.log("Data in ReMixChart:", data);
    console.log("Data type:", typeof data);
    console.log("Is Array:", Array.isArray(data));
    if (Array.isArray(data)) {
      console.log("Data length:", data.length);
    }
  }, [data]);

  const handleLegendClick = useCallback((legend: LegendKey) => {
    if (disableLegendClick) return;
    setSelectedLegends((prevSelectedLegends) =>
      prevSelectedLegends.some((item) => item.dataKey === legend.dataKey)
        ? prevSelectedLegends.filter((item) => item.dataKey !== legend.dataKey)
        : [...prevSelectedLegends, legend]
    );
  }, []);
  useEffect(() => {
    if (onLegendChange) {
      onLegendChange(selectedLegends);
    }
  }, [selectedLegends]);
  useEffect(() => {
    if (
      selectedLegends.length === legends.length &&
      selectedLegends.length !== 0
    ) {
      setSelectedLegends([]);
    }
  }, [selectedLegends]);
  useEffect(() => {
    console.log("Data Received", data);

    setSelectedLegends([]);
  }, [data]);

  const filteredLegends = useMemo(
    () => legends.filter((e) => e.dataKey !== xDataKey),
    [legends, xDataKey]
  );

  const orderedSelectedLegends = useMemo(() => {
    if (selectedLegends.length === 0) return filteredLegends;
    return legends.filter((legend) =>
      selectedLegends.some((selected) => selected.dataKey === legend.dataKey)
    );
  }, [selectedLegends, filteredLegends, legends]);

  if (isMobile) {
    barCategoryGap = barCategoryGap / 3;
    fontSize = fontSize - 5;
    yAxisWidth = 40;
    secondYAxisWidth = 40;
    xAxisAngle = xAxisAngle ?? 30;
  }

  return (
    <>
      <div className="chart-container">
        <ResponsiveContainer>
          <ComposedChart
            barCategoryGap={barCategoryGap}
            syncId={syncid}
            data={data || []}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              tick={xTick}
              height={xaxisHeight}
              tickSize={9.3}
              angle={xAxisAngle}
              tickMargin={xAxisTickMargin}
              fontWeight={500}
              fontSize={fontSize}
              interval={xTick ? 0 : undefined}
              minTickGap={8}
              dataKey={xDataKey}
            >
              {
                <Label
                  fontSize={labelFontSize ?? fontSize + 3}
                  fill={SecondaryColor}
                  offset={xAxisLabelOffset}
                  value={xLabel}
                  // height={40}
                  position={xAxisLabelPosition}
                />
              }
            </XAxis>
            {secondXDataKey && (
              <XAxis
                dataKey={secondXDataKey}
                axisLine={false}
                tickLine={false}
                fontSize={fontSize}
                interval={0}
                tick={rendexSecondXTick ?? (renderQuarterTick as any)}
                height={secondXAxisHeight}
                xAxisId="quarter"
              >
                {secondXAxisLabel && (
                  <Label
                    fontSize={fontSize + 3}
                    fill={SecondaryColor}
                    value={secondXAxisLabel}
                    style={{ textAnchor: "middle" }}
                  />
                )}
              </XAxis>
            )}
            <YAxis
              tickFormatter={YAxisFormatter}
              fontSize={fontSize}
              fontWeight={500}
              width={yAxisWidth}
            >
              <Label
                fontSize={labelFontSize ?? fontSize + 3}
                fill={SecondaryColor}
                value={yAxisLabel}

                fontWeight={500}
                angle={-90}
                position="insideLeft"
                style={{ textAnchor: "middle" }}
              />
            </YAxis>
            {(secondYAxisLabel ||
              legends.filter((l) => l.type === ChartType.Bar).length == 0) && (
              <YAxis
                yAxisId="right"
                orientation="right"
                fontSize={fontSize}
                width={secondYAxisWidth}
                tickFormatter={YAxisFormatter}
              >
                <Label
                  fontSize={labelFontSize ?? fontSize + 3}
                  value={secondYAxisLabel}
                  position="insideRight"
                  fontWeight={500}
                  fill={SecondaryColor}
                  angle={-90}
                  style={{ textAnchor: "middle" }}
                />
              </YAxis>
            )}
            {showBrush && (
              <Brush

                dataKey={xDataKey}
                height={18}
                stroke={brushHeight == 1 ? "transparent" : COLOR_CODES.primary}
                startIndex={brushIndex?.startIndex ?? undefined}
                endIndex={brushIndex?.endIndex ?? undefined}
                floodColor={COLOR_CODES.primary}
              />
            )}
            <Tooltip
              contentStyle={{
                fontSize: `${fontSize}px`,
                fontWeight: 500,
              }}
              labelStyle={{
                fontSize: `${fontSize + 1}px`,
              }}
              labelFormatter={
                (labelFormatter as any) ??
                ((value, payload) => {
                  try {
                    if (secondXDataKey && payload[0].payload[secondXDataKey]) {
                      if (isTimeSlot) {
                        // return [getTimeRange(parseInt(value)) + ` (${value})`];
                        return [
                          ` ${payload[0].payload[secondXDataKey]} - ${
                            payload[0].payload[xDataKey]
                          } (${getTimeRange(
                            parseInt(payload[0].payload[xDataKey])
                          )}) `,
                        ];
                      }
                      return [
                        ` ${payload[0].payload[secondXDataKey]} - ${payload[0].payload[xDataKey]} `,
                      ];
                    }
                    if (onlyTitle) {
                      return [value];
                    }
                    if (isTimeSlot) {
                      return [getTimeRange(parseInt(value)) + ` (${value})`];
                    }

                    if (!payload[0].payload.date) {
                      return [value];
                    }
                    return [`${payload[0].payload.date} - Time Slot ${value}`];
                  } catch {
                    if (isTimeSlot) {
                      return [value, getTimeRange(parseInt(value))];
                    }
                    return [value];
                  }
                })
              }
              formatter={(value, name) => {
                let val = `${parseFloat(value.toString())
                  .toFixed(2)
                  .toString()}`;
                if (hidePrice) {
                  return val.replace("Price", "");
                }
                const tool_name =
                  nameFormatter != undefined
                    ? nameFormatter(name as any)
                    : name;
                // console.log(value)
                return [val, tool_name];
              }}
            />

            {orderedSelectedLegends.map((legend, index) =>
              legend.type === ChartType.Bar ? (
                <Bar
                  key={index}
                  fill={getLegendColor(
                    legend,
                    legends.findIndex((l) => l.dataKey === legend.dataKey),
                    selectedLegends
                  )}
                  radius={BAR_RADIUS}
                  unit={" "+legend.unit}

                  type="monotone"
                  maxBarSize={maxBarSize}
                  name={legend.name}
                  stackId={legend.stackId}
                  dataKey={legend.dataKey}
                  yAxisId={legend.yAxisId}
                  stroke={getLegendColor(
                    legend,
                    legends.findIndex((l) => l.dataKey === legend.dataKey),
                    selectedLegends
                  )}
                  strokeWidth={2}
                >
                  {Array.isArray(data) && showBarLabel && (
                    <LabelList
                      dataKey={legend.dataKey}
                      position="insideTop"
                      fill="#FFFFFF"
                      fontSize={barFontSize ?? fontSize}
                      angle={data.length <= 5 || perpendicularBarLab ? 0 : 90}
                      offset={5}
                      style={{
                        textAnchor:
                          data.length <= 5 || perpendicularBarLab
                            ? "middle"
                            : "start",
                        fontWeight: "bold",
                      }}
                      formatter={(value: number) => {
                        if (!value || value < 5) return "";
                        return value.toFixed(2);
                      }}
                    />
                  )}
                </Bar>
              ) : legend.type === ChartType.Area ? (
                <Area
                  key={index}
                  type="monotone"
                  name={legend.name}
                  unit={" "+legend.unit}
                  stackId={legend.stackId}
                  dataKey={legend.dataKey}
                  yAxisId={legend.yAxisId}
                  fill={getLegendColor(
                    legend,
                    legends.findIndex((l) => l.dataKey === legend.dataKey),
                    selectedLegends
                  )}
                  stroke={getLegendColor(
                    legend,
                    legends.findIndex((l) => l.dataKey === legend.dataKey),
                    selectedLegends
                  )}
                  strokeWidth={3}
                />
              ) : (
                <Line
                  key={index}
                  type="monotone"
                  name={legend.name}
                  unit={" "+legend.unit}
                  yAxisId={legend.yAxisId}
                  dataKey={legend.dataKey}
                  stroke={getLegendColor(
                    legend,
                    legends.findIndex((l) => l.dataKey === legend.dataKey),
                    selectedLegends
                  )}
                  dot={false}
                  strokeWidth={2}
                  strokeDasharray={legend.isDash ? "5 5" : undefined}
                />
              )
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {!disableLegend && (
        <div className="flex flex-wrap justify-center gap-x-2 mt-3 ">
          {filteredLegends.map((legend, index) => {
            return (
              <React.Fragment key={index}>
                <div className="mb-1">
                  {LegendComponent(
                    legend,
                    index,
                    selectedLegends,
                    handleLegendClick,
                    circleLegend
                  )}
                </div>
                {legendBreakIndex != 0 && legendBreakIndex === index && (
                  <div style={{ width: "100%", height: "2px" }}></div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </>
  );
}
